import { HTTPClient } from "@abaco/web-common/src/HTTP";
import { RegisterType, TimesheetHeader } from "../models/RegistersModel";
import { DailyWorkerActivities, ExecutionInfoResponse, Timesheet } from "../models/TimesheetBodyModel";

export default class TimesheetRegisterApi {
    private http: HTTPClient;
    private brgRoot: string;
    private timesheetRoot: string;

    constructor(http: HTTPClient, brgRoot: string, timesheetRoot: string) {
        this.http = http;
        this.brgRoot = brgRoot;
        this.timesheetRoot = timesheetRoot;
    }

    public async saveTimesheet(pk_cuaa: number, data: TimesheetHeader): Promise<TimesheetHeader> {
        const res = await this.http.post(`${this.timesheetRoot}/v1/timesheet/${pk_cuaa}`, data);
        return res.data as TimesheetHeader;
    }

    public async getRegisterTypes(): Promise<RegisterType[]> {
        const res = await this.http.get(`${this.brgRoot}/v1/brgTypes/list`);
        return res.data.records;
    }

    public async getFilteredTimesheet(pk_cuaa: number): Promise<TimesheetHeader[]> {
        const res = await this.http.get(`${this.timesheetRoot}/v1/timesheet/${pk_cuaa}`);
        return res.data;
    }

    public async getMontlyTimesheet(pk_cuaa: number, tsId: number, year: number, month: number): Promise<Timesheet> {
        const res = await this.http.get(`${this.timesheetRoot}/v1/timesheet/${pk_cuaa}/${tsId}/${year}/${month}`);
        return res.data;
    }

    // /timesheet/415907/1071/from/2023-06-01/to/2023-06-30
    public async getRangeTimesheet(pk_cuaa: number, tsId: number, fdate: string, tdate: string): Promise<Timesheet> {
        const res = await this.http.get(
            `${this.timesheetRoot}/v1/timesheet/${pk_cuaa}/${tsId}/from/${fdate}/to/${tdate}`
        );
        return res.data;
    }

    public async massiveImport(
        pk_cuaa: number,
        tsId: number,
        year: number,
        month: number,
        overwrite: boolean
    ): Promise<Timesheet> {
        const res = await this.http.patch(
            `${this.timesheetRoot}/v1/timesheet/${pk_cuaa}/${tsId}/massiveImport/${year}/${month}?overwriteExecution=${overwrite}`
        );
        return res.data;
    }

    public async updateExecution(
        pk_cuaa: number,
        subjectId: number,
        tsId: number,
        year: number,
        month: number,
        day: number,
        timesheetExecutionId: number,
        activityId: number,
        lotId: number | null,
        blockId: number | null,
        notes: string | null,
        workedHours: number | null
    ): Promise<ExecutionInfoResponse> {
        let params: any = { activityId: activityId };
        if (notes) {
            params = { ...params, notes: notes };
        }
        params = { ...params, lotId: lotId };
        params = { ...params, blockId: blockId };
        if (workedHours !== null && workedHours !== undefined) {
            params = { ...params, workingHours: workedHours };
        }

        const res = await this.http.put(
            `${this.timesheetRoot}/v1/timesheet/${pk_cuaa}/${tsId}/${year}/${month}/${day}/${subjectId}/${timesheetExecutionId}`,
            params
        );

        return res.data;
    }

    public async deleteExecutionFromActivity(
        pk_cuaa: number,
        subjectId: number,
        tsId: number,
        year: number,
        month: number,
        day: number,
        timesheetExecutionId: number,
        deleteFromActivities: boolean
    ): Promise<ExecutionInfoResponse> {
        const param = deleteFromActivities ? "?removeFromAP=TRUE" : "";
        const res = await this.http.delete(
            `${this.timesheetRoot}/v1/timesheet/${pk_cuaa}/${tsId}/${year}/${month}/${day}/${subjectId}/${timesheetExecutionId}${param}`
        );
        return res.data;
    }

    public async createExecution(
        pk_cuaa: number,
        subjectId: number,
        tsId: number,
        year: number,
        month: number,
        day: number,
        newExeData: any
    ): Promise<ExecutionInfoResponse> {
        const res = await this.http.post(
            `${this.timesheetRoot}/v1/timesheet/${pk_cuaa}/${tsId}/${year}/${month}/${day}/${subjectId}`,
            newExeData
        );
        return res.data;
    }

    public async getDailyTimesheet(
        pk_cuaa: number,
        subjectId: number,
        tsId: number,
        year: number,
        month: number,
        day: number
    ): Promise<DailyWorkerActivities> {
        const res = await this.http.get(
            `${this.timesheetRoot}/v1/timesheet/${pk_cuaa}/${tsId}/${year}/${month}/${day}/${subjectId}`
        );
        return res.data as DailyWorkerActivities;
    }

    public async deleteTimesheet(pk_cuaa: number, draftheaderId: number): Promise<boolean> {
        let operationSuccesfully = false;
        try {
            await this.http.delete(`${this.timesheetRoot}/v1/timesheet/${pk_cuaa}/${draftheaderId}`);
            operationSuccesfully = true;
        } catch (error) {
            operationSuccesfully = false;
        }
        return operationSuccesfully;
    }

    public async modifyTimesheetHeader(pk_cuaa: number, brgId: number, data: any): Promise<TimesheetHeader> {
        const res = await this.http.put(`${this.timesheetRoot}/v1/timesheet/${pk_cuaa}/${brgId}`, data);
        return res.data;
    }

    public async canDoAction(pkCuaa: number, competence: string): Promise<boolean> {
        const url = `/sitiagri-rest-api/api/v1/user_registry/subjects/${pkCuaa}/module_competencies/TIMESHEET/${competence}`;
        const res = await this.http.get(url);
        return res.data.result;
    }

    public async checkTimesheet(
        pk_cuaa: number,
        tsId: number,
        year: number,
        month: number,
        timesheet: Timesheet
    ): Promise<Timesheet> {
        const res = await this.http.patch(
            `${this.timesheetRoot}/v1/timesheet/${pk_cuaa}/${tsId}/check/${year}/${month}`,
            timesheet
        );
        return res.data as Timesheet;
    }
}
